import {
  InvestigationController_outletItems_v1,
  InvestigationController_parkItems_v1,
  type ItemActivityDayResource,
  type ItemActivityResource,
} from '@repo/api-client';
import { TableCell, TableRow } from '@repo/ui/components/table';

import { formatPaise } from '@/lib/money';

import { Screen } from '../back-office/Screen';
import { DayLabel, FlowMeasures, ItemNameCell } from './item-cells';
import { ItemReportControls, ItemReportTable } from './item-report';
import { type ItemReportReads, useItemReport } from './use-item-report';

/**
 * What each thing in the catalogue did over a period, and whether the shelf
 * agrees: `GET /v1/reports/items` and
 * `GET /v1/reports/outlets/{outletId}/items`.
 *
 * ## The one column that must never be added up
 *
 * `stock.countedQuantity` is a **level**, not a flow. Every other measure on a
 * row records movement and therefore adds up across days; this one is the
 * latest count inside the period, which is a photograph of the shelf. Ten
 * counted on Monday and ten counted on Friday is ten, and a per-day chart or a
 * footer that answered `20` there would be wrong in a way nobody notices until
 * a stock count is disputed — at which point the screen is the thing being
 * disputed with.
 *
 * So **nothing on this screen is derived from the day list**. Every figure in
 * every cell, range row and day row alike, is read off the field the API
 * published for exactly that row. `item-activity.ts` beside this file holds the
 * readings and, deliberately, no function that totals anything.
 *
 * There is no footer row either, and that is a second decision rather than the
 * same one. A total down a column of items is meaningless whatever the measure:
 * three hundred chai and forty admissions do not add to three hundred and
 * forty of anything. `Items.test.tsx` asserts the day rule against a fixture no
 * derivation can satisfy — three days of `10` under a range figure of `7`,
 * which no sum, maximum or last-one-wins produces.
 *
 * ## Five measures, and nothing netted
 *
 * Sold, refunded, delivered, counted and variance are five different numbers.
 * The point of this read is that an investigator sees each of them, so
 * sold-minus-refunded appears nowhere and neither does any other subtraction
 * the API declined to make. Where a quantity has money attached — sold, refunded,
 * voided — the two sit in one cell, one above the other, which is a pairing and
 * not an arithmetic: both figures are printed and neither is folded into the
 * other. `Attribution.tsx`'s rate cell sets the same idiom for the same reason.
 *
 * The four write-off reasons are the one place a total is drawn, and it is drawn
 * with its parts beneath it: `−6` alone cannot be told from six broken, and six
 * stolen is a different conversation. The delivery stays out of that total and
 * keeps its own column, because it is one of the five.
 *
 * ## Owner only, and why
 *
 * Both routes declare `@RequireRole('OWNER')` and both carry `costPaise` and
 * `marginPaise`. A manager is refused `403 AUTH_FORBIDDEN` before either handler
 * runs, and there is no partial response — so `route-table.tsx` declares
 * `requireRole('OWNER')` and the navigation does not offer this to anybody else.
 * The screen still has a state for a refusal arriving: `role` is restored from
 * `sessionStorage` and is attacker-editable, so the guard decides what is drawn
 * and the server decides what is served.
 *
 * ## The two shapes of one question
 *
 * "Every outlet in reach" is the park read, which sums each item across every
 * outlet the caller can see. Naming an outlet is a different route with the
 * outlet in its path, and it answers with an envelope the park read has no
 * counterpart for: `carriesStock`, published once so a column of `Not stocked`
 * has its reason stated rather than inferred.
 *
 * ## What is deliberately not here
 *
 * **A CSV export.** No line of this ticket asks for one, `cash-book-export.ts`
 * and `profit-and-loss-export.ts` are the two screens that do, and a third path
 * built speculatively is a third place for the counted column to be summed.
 * Named in the PR body.
 *
 * ## A disagreement that is not this screen's to settle
 *
 * On a day carrying a repeat stocktake, this read and Day Close can publish
 * different variance counts (`blossom-rest-api#454`, open). They are two honest
 * answers to two slightly different questions and nothing here papers over the
 * gap: this screen prints what this read published, and Day Close prints what
 * that one did.
 */

/**
 * The owner's two routes. Module scope, so `useItemReport` does not refire on
 * every render.
 */
const READS: ItemReportReads<ItemActivityResource> = {
  park: (client, range, signal) =>
    client.request(InvestigationController_parkItems_v1, { query: range, signal }),
  outlet: (client, outletId, range, signal) =>
    client.request(InvestigationController_outletItems_v1, {
      path: { outletId },
      query: range,
      signal,
    }),
};

export function Items() {
  const report = useItemReport(READS);

  return (
    <Screen
      title="Items"
      lede="what each thing did, and what the shelf says"
      description="What every item in the catalogue sold, cost and made over a period, beside what was delivered, written off and counted. Nothing is netted: sold and refunded are two figures, and the count is the latest one taken rather than a total of the days."
      aside={<ItemReportControls report={report} />}
    >
      <ItemReportTable
        report={report}
        label="Item activity"
        columns={COLUMNS}
        forbidden={{
          title: 'This account cannot read item activity',
          description:
            'This report carries what each item cost and what it made, so it is the owner’s alone. If that is what you are, sign out and back in — the role this browser is holding may be out of date.',
        }}
      >
        {(items) => items.map((row) => <ItemRows key={row.catalogueItemId} item={row} />)}
      </ItemReportTable>
    </Screen>
  );
}

/**
 * The columns, and the five that are deliberately separate.
 *
 * Sold, Refunded, Delivered, Counted and Variance are five different numbers
 * with five different meanings, and there is no column netting any pair of them
 * together. They are kept adjacent, in that order, because seeing each of them
 * beside the others is the whole of what this read is for.
 *
 * **The money sits after them, and that ordering is a measured decision rather
 * than a preference.** Nine columns of Indian-format currency do not fit a 1280
 * window, so something scrolls; what must not scroll is the pair the ticket is
 * about. Counted and Variance were the last two columns in the first draft and
 * were off the right edge at a desk width, which is the worst possible place for
 * them. Margin — with the cost it was worked out from beneath it — is the one
 * that gives way instead, and at 1600 nothing does.
 *
 * Everything before Margin is `item-cells.tsx`'s and is shared with
 * `ItemSales`, which is these columns without the last one.
 */
const COLUMNS = [
  { label: 'Item' },
  { label: 'Sold', numeric: true },
  { label: 'Refunded', numeric: true },
  { label: 'Voided', numeric: true },
  { label: 'Delivered', numeric: true },
  { label: 'Written off', numeric: true },
  { label: 'Counted', numeric: true },
  { label: 'Variance', numeric: true },
  { label: 'Margin', numeric: true },
] as const;

/**
 * One item's range row, and its day breakdown under it when one was asked for.
 *
 * A fragment of rows rather than a nested table: a day is the same measures over
 * a shorter period, so they belong in the same columns, and a reader comparing a
 * Tuesday against the month should not have to compare across two grids to do
 * it.
 *
 * **Every figure below comes from the row it is drawn for.** The range row reads
 * `item`, each day row reads its own entry, and nothing is derived from
 * anything — which is what keeps the counted column a level rather than a
 * running total. See this file's head.
 */
function ItemRows({ item }: { item: ItemActivityResource }) {
  return (
    <>
      <TableRow>
        <ItemNameCell code={item.code} name={item.name} retired={item.retired} />
        <FlowMeasures figures={item} />
        <MarginAndCost margin={item.marginPaise} cost={item.costPaise} />
      </TableRow>

      {item.days?.map((entry) => (
        <ItemDayRow key={entry.businessDate} day={entry} />
      ))}
    </>
  );
}

/**
 * One business day of one item's figures.
 *
 * The flows here add up to the row above — the API says so and computes both
 * ends. The count does not, and this row prints the day's own count exactly as
 * it arrived: a shelf holding ten on each of three days held ten, and the row
 * above it says ten too.
 */
function ItemDayRow({ day }: { day: ItemActivityDayResource }) {
  return (
    <TableRow>
      <DayLabel businessDate={day.businessDate} state={day.state} />
      <FlowMeasures figures={day} />
      <MarginAndCost margin={day.marginPaise} cost={day.costPaise} />
    </TableRow>
  );
}

/**
 * What was made, over what it cost.
 *
 * One column rather than two, and both figures printed. `marginPaise` is
 * `soldValuePaise − costPaise` and gross of refunds and voids, exactly as the
 * API publishes it — nothing here recomputes it, and no netted figure is
 * invented from the refund and void columns beside it. The cost is the smaller
 * line because it is the working rather than the answer, and it is labelled
 * because two amounts stacked with no words between them could be read either
 * way round.
 *
 * These two are what make the whole report the owner's: both routes carry them,
 * and both refuse a manager because of it.
 */
function MarginAndCost({ margin, cost }: { margin: number; cost: number }) {
  return (
    <TableCell className="text-right">
      <span className="sr-only">
        Margin {formatPaise(margin)}, on a cost of {formatPaise(cost)}
      </span>
      <span aria-hidden className="block font-mono text-foreground numeric">
        {formatPaise(margin)}
      </span>
      <span aria-hidden className="block font-mono text-xs text-muted-foreground numeric">
        {formatPaise(cost)} cost
      </span>
    </TableCell>
  );
}
